import { type CollaboratorPermissionEntry } from "@vega-api/generated/types/collaboratorPermissionEntry";
import { RoleContext } from "@vega-api/generated/types/roleContext";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { SubjectType } from "@vega-api/generated/types/subjectType";
import { type BaseMultiSelectOption } from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { m } from "@vega-core/paraglide/messages";
import { convertToTitleCase } from "@vega-core/utils/formatString";

export const FUND_VEHICLE_TAB = {
  ALL: "all",
  EMPLOYEE: "employee",
  NON_EMPLOYEE: "non-employee",
} as const;

export type FundVehicleTab =
  (typeof FUND_VEHICLE_TAB)[keyof typeof FUND_VEHICLE_TAB];

export type CollaboratorsV2State = {
  id: string;
  index: number;
  selectedRole: BaseMultiSelectOption;
  fundVehicleTab: FundVehicleTab;
};

export type PendingRoleCreation = {
  roleId: string;
  fundVehicleTab: FundVehicleTab;
};

export type EditedPermission = {
  roleId: string;
  fundVehicleTab: FundVehicleTab;
};

export const FUND_VEHICLE_TAB_TO_SUBJECT_TYPE: Record<
  FundVehicleTab,
  SubjectType
> = {
  [FUND_VEHICLE_TAB.ALL]: SubjectType.FUND_VEHICLE,
  [FUND_VEHICLE_TAB.EMPLOYEE]: SubjectType.FUND_VEHICLE_EMPLOYEE,
  [FUND_VEHICLE_TAB.NON_EMPLOYEE]: SubjectType.FUND_VEHICLE_NON_EMPLOYEE,
};

export const SUBJECT_TYPE_TO_FUND_VEHICLE_TAB: Partial<
  Record<SubjectType, FundVehicleTab>
> = {
  [SubjectType.FUND_VEHICLE]: FUND_VEHICLE_TAB.ALL,
  [SubjectType.FUND_VEHICLE_EMPLOYEE]: FUND_VEHICLE_TAB.EMPLOYEE,
  [SubjectType.FUND_VEHICLE_NON_EMPLOYEE]: FUND_VEHICLE_TAB.NON_EMPLOYEE,
};

export const fundVehicleTagConfig: Record<
  FundVehicleTab,
  { label: () => string; variant: TagVariants }
> = {
  [FUND_VEHICLE_TAB.ALL]: {
    label: () => m.settings_collaborators_inviteCollaboratorAllFundVehicles(),
    variant: TagVariants.Positive,
  },
  [FUND_VEHICLE_TAB.EMPLOYEE]: {
    label: () => m.settings_collaborators_inviteCollaboratorEmployeeRelated(),
    variant: TagVariants.Data2,
  },
  [FUND_VEHICLE_TAB.NON_EMPLOYEE]: {
    label: () =>
      m.settings_collaborators_inviteCollaboratorNonEmployeeRelated(),
    variant: TagVariants.Warning,
  },
};

export const FUND_VEHICLE_ORDER: Record<FundVehicleTab, number> = {
  [FUND_VEHICLE_TAB.ALL]: 0,
  [FUND_VEHICLE_TAB.EMPLOYEE]: 1,
  [FUND_VEHICLE_TAB.NON_EMPLOYEE]: 2,
};

export const getFundVehicleTag = (fundVehicleTab: FundVehicleTab) => {
  const config = fundVehicleTagConfig[fundVehicleTab];

  return <Tag variant={config.variant}>{config.label()}</Tag>;
};

export const hasWorkbenchVehicleContext = (role: RoleNew): boolean =>
  role.subjectTypeContexts?.includes(RoleContext.WORKBENCH_VEHICLE) ?? false;

const roleMatchesFundVehicleTab = (
  roleSubjectType: string | undefined,
  fundVehicleTab: FundVehicleTab,
): boolean => {
  if (fundVehicleTab === FUND_VEHICLE_TAB.ALL) {
    return (
      roleSubjectType === undefined ||
      roleSubjectType === SubjectType.FUND_VEHICLE
    );
  }

  return roleSubjectType === FUND_VEHICLE_TAB_TO_SUBJECT_TYPE[fundVehicleTab];
};

export const getRoleOptionsForFundVehicleTab = (
  roles: RoleNew[],
  fundVehicleTab: FundVehicleTab,
): BaseMultiSelectOption[] => {
  const seen = new Set<string>();

  return roles.reduce<BaseMultiSelectOption[]>((options, role) => {
    if (seen.has(role.id)) return options;

    if (
      hasWorkbenchVehicleContext(role) &&
      roleMatchesFundVehicleTab(role.subjectType, fundVehicleTab)
    ) {
      seen.add(role.id);
      options.push({
        label: convertToTitleCase(role.name),
        value: role.id,
      });
    }

    return options;
  }, []);
};

export const getCollaboratorRoleOptions = (
  roles: RoleNew[],
): BaseMultiSelectOption[] => {
  const seen = new Set<string>();

  return roles.reduce<BaseMultiSelectOption[]>((options, role) => {
    if (
      seen.has(role.id) ||
      !role.subjectTypeContexts?.includes(RoleContext.WORKBENCH)
    ) {
      return options;
    }

    seen.add(role.id);
    options.push({
      id: role.id,
      label: convertToTitleCase(role.name),
      value: role.id,
    });

    return options;
  }, []);
};

// ─── Shared Subject Types & Utilities ───────────────────────────────────────

export const COLLABORATOR_FUND_SUBJECT_TYPES: SubjectType[] = [
  SubjectType.FUND_VEHICLE,
  SubjectType.FUND_VEHICLE_EMPLOYEE,
  SubjectType.FUND_VEHICLE_NON_EMPLOYEE,
];

export const FUND_VEHICLE_TAB_SELECTED_STYLES: Record<FundVehicleTab, string> =
  {
    [FUND_VEHICLE_TAB.ALL]:
      "bg-text/positive/20 border-transparent text-text/primary",
    [FUND_VEHICLE_TAB.EMPLOYEE]:
      "bg-data-2/20 border-transparent text-text/primary",
    [FUND_VEHICLE_TAB.NON_EMPLOYEE]:
      "bg-text/warning/20 border-transparent text-text/primary",
  };

export const deduplicatePermissionsByRoleId = (
  permissions: CollaboratorPermissionEntry[],
): CollaboratorPermissionEntry[] =>
  Array.from(
    permissions
      .reduce((byRoleId, permission) => {
        if (permission.roleId && !byRoleId.has(permission.roleId)) {
          byRoleId.set(permission.roleId, permission);
        }

        return byRoleId;
      }, new Map<string, CollaboratorPermissionEntry>())
      .values(),
  );

export const getUniqueRoleNames = (
  permissions?: CollaboratorPermissionEntry[],
): string[] => {
  if (!permissions?.length) return [];

  return Array.from(
    new Set(
      permissions
        .map((role) => role.roleName)
        .filter((roleName): roleName is string => roleName !== undefined),
    ),
  );
};

const MAX_VISIBLE_ROLES = 2;

export function CollaboratorRolesCell({
  permissions,
}: {
  permissions?: CollaboratorPermissionEntry[];
}) {
  const uniqueRoles = getUniqueRoleNames(permissions);

  if (!uniqueRoles.length) return <>-</>;

  const visibleRoles = uniqueRoles.slice(0, MAX_VISIBLE_ROLES);
  const remainingRoles = uniqueRoles.slice(MAX_VISIBLE_ROLES);

  return (
    <div className="flex gap-3 flex-nowrap lowercase py-4">
      {visibleRoles.map((roleName) => (
        <Tag key={roleName} variant={TagVariants.Regular}>
          {convertToTitleCase(roleName)}
        </Tag>
      ))}
      {remainingRoles.length > 0 && (
        <Tooltip
          content={
            <div className="flex flex-col gap-1">
              {remainingRoles.map((role) => (
                <Tag key={role} variant={TagVariants.Regular}>
                  {convertToTitleCase(role)}
                </Tag>
              ))}
            </div>
          }
        >
          <span className="cursor-pointer">
            <Tag key="remaining-roles" variant={TagVariants.Regular}>
              +{remainingRoles.length}
            </Tag>
          </span>
        </Tooltip>
      )}
    </div>
  );
}

export const getFundVehicleTabFromPermission = (
  permission: CollaboratorPermissionEntry,
): FundVehicleTab => {
  if (permission.roleSubjectType) {
    const mapped = SUBJECT_TYPE_TO_FUND_VEHICLE_TAB[permission.roleSubjectType];

    if (mapped) return mapped;
  }

  return (
    (
      permission as CollaboratorPermissionEntry & {
        fundVehicleTab?: FundVehicleTab;
      }
    ).fundVehicleTab ?? FUND_VEHICLE_TAB.ALL
  );
};
